import React, { useCallback, useEffect, useState } from 'react';
import FormInput from '../../../shared/components/ui/FormInput';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import Button from '../../../shared/components/ui/Button';
import { PreviewRateUnitID } from './PreviewRateUnitID';
import { PreviewRateAccommodationId } from './PreviewRateAccommodationId';
import {
  createPricing,
  getAllAccommodations,
  getAllAccommodationsByProject,
  getAllBlocks,
  getPlc,
  getPlcbyProjectId,
  getAllPricingByProjectId,
  getUniversalMeasurementByProject
} from '../../services/api/implementation/impl';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import Select from 'react-select';
import mixpanel from 'mixpanel-browser';
import { PROJECT_SETUP_TYPES } from '../../utils/projectStructurePriorities';
import { triggerProjectStructureReview } from '../../utils/triggerProjectStructureReview';

/*
 * CreateRateList — form for creating a new pricing row for an accommodation.
 *
 * Three rate types are supported:
 *
 *  1. Cookie-based  (e.g. "sq/ft Based" or "sq/mt Based")
 *     - subCategory value: measurementTypeWithoutSlash (e.g. "sqft" or "sqmt")
 *     - Price entered directly in cookie units.
 *     - No conversion — price sent as-is to backend.
 *
 *  2. Accommodation-based
 *     - subCategory value: "accommodation"
 *     - Flat price (not per area unit).
 *     - No conversion — price sent as-is to backend.
 *
 *  3. Universal Measurement (UM) based  (e.g. "tago Based")
 *     - subCategory value: "um_{id}"  (e.g. "um_15")
 *     - Dropdown shows project-scoped UMs from backend.
 *     - On SAVE → forward-convert: entered price ÷ per_unit = cookie-unit price sent to backend.
 *       e.g. ₹1000/tago ÷ 10 (per_unit) = ₹100/sq/ft stored.
 *     - universal_measurement_id is included in the payload so the backend and display
 *       know which UM was used.
 *
 * Conversion precision: toFixed(8) — avoids integer rounding that causes reverse-display errors.
 */

export const CreateRateList = ({
  projectName,
  createSuccess,
  pricingType,
  onClose,
  initialSubCategory = ''
}) => {
  const userId = Cookies.get('userId');

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm();

  // Cookie-based measurement unit for this company — 'sq/ft' or 'sq/mt'
  const rawMeasurementUnit = Cookies.get('measurement_type');
  const measurementType =
    !rawMeasurementUnit || rawMeasurementUnit === 'null' || rawMeasurementUnit === 'undefined'
      ? 'sq/ft'
      : rawMeasurementUnit;
  // Used as dropdown value for the cookie-based option (no slash: "sqft" | "sqmt")
  const measurementTypeWithoutSlash = measurementType.replace('/', '');

  const [selectedAccommodationType, setSelectedAccommodationType] = useState('GROUP');
  const [selectedAccommodation, setSelectedAccommodation] = useState(null);
  // subCategory drives which rate type is active:
  //   ''                    → nothing selected yet
  //   measurementTypeWithoutSlash → cookie-based (e.g. 'sqft')
  //   'accommodation'       → flat price
  //   'um_{id}'             → universal measurement (e.g. 'um_15')
  const [subCategory, setSubCategory] = useState('');
  const [onProccedbtn, setProccedbtn] = useState('');
  const { id: projectId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [accommodationListOptions, setAccommodationListOptions] = useState([]);
  const [blocksListOptions, setBlocksListOptions] = useState([]);
  const [unitListOptions, setUnitListOptions] = useState([]);
  const [PLCListOptions, setPLCListOptions] = useState([]);
  const [selectedBlockId, setSelectedBlockId] = useState('');

  // Preview states — holds payload and display data before final submission
  const [formPayload, setFormPayload] = useState({});
  const [previewData, setPreviewData] = useState([]);

  // Fetch accommodations for the selected project and accommodation type
  const { data: accommodations, refetch: refetchAccommodations } = useQuery({
    queryKey: ['accommodations', projectId, selectedAccommodationType],
    queryFn: () =>
      getAllAccommodationsByProject(projectId, `&accomodationType=${selectedAccommodationType}`),
    enabled: !!projectId && !!selectedAccommodationType
  });

  // Fetch all existing pricing to filter out accommodations that already have a rate list
  const { data: getAllPricingList } = useQuery({
    queryKey: ['getAllPricingList', projectId],
    queryFn: () => getAllPricingByProjectId(projectId),
    enabled: !!projectId
  });

  const { data: blocksDyna, refetch: refetchBlocks } = useQuery({
    queryKey: ['blocks', projectId],
    queryFn: () => getAllBlocks(projectId),
    enabled: !!projectId
  });

  const { data: PLCbyProjectIdList, refetch: refetchPLCProject } = useQuery({
    queryKey: ['PLCbyProjectIdList'],
    queryFn: () => getPlc(),
    enabled: !!projectId
  });

  // Fetch universal measurements for this project
  // Used to populate the Rate Type dropdown with UM options
  const { data: universalMeasurements = [] } = useQuery({
    queryKey: ['universalMeasurementsByProject', projectId],
    queryFn: () => getUniversalMeasurementByProject(projectId),
    enabled: !!projectId
  });

  // True when the selected rate type is a universal measurement (subCategory starts with 'um_')
  const isUmBased = subCategory.startsWith('um_');
  // The full UM object for the selected subCategory (e.g. { id:15, name:'tago', per_unit:'10', measurement_type:'sq/ft' })
  const selectedUm = isUmBased
    ? universalMeasurements.find((um) => `um_${um.id}` === subCategory)
    : null;

  // Sync subCategory when parent passes a new initialSubCategory (e.g. from model cards)
  useEffect(() => {
    setSubCategory(initialSubCategory || '');
    setSelectedAccommodation(null);
  }, [initialSubCategory]);

  useEffect(() => {
    mixpanel.track('Create Rate List Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  /*
   * Build the accommodation dropdown options.
   * Filters out accommodations that already have any pricing row for this accommodation type
   * so each accommodation can only have one rate list at a time.
   */
  useEffect(() => {
    if (accommodations && getAllPricingList) {
      const accommodationNameList = Object.values(accommodations).flat();
      if (accommodationNameList.length > 0) {
        // Collect IDs of accommodations that already have a rate list for this type
        const accommodationsWithPricing = new Set(
          getAllPricingList
            .filter(
              (pricing) => pricing.accommodation?.accomodationType === selectedAccommodationType
            )
            .map((pricing) => pricing.accommodation_id)
        );

        // Only show accommodations without an existing rate list
        const availableAccommodations = accommodationNameList.filter(
          (item) =>
            !accommodationsWithPricing.has(item.id) &&
            item.accomodationType === selectedAccommodationType
        );

        setAccommodationListOptions(
          availableAccommodations.map((item) => ({
            value: item.id,
            label: item.name,
            type: item.accomodationType,
            buildUpArea: item.buildUpArea ?? item.build_up_area ?? '',
            plotArea: item.plotArea ?? item.plot_area ?? '',
            loadingFactor: item.loadingFactor ?? item.loading_factor ?? ''
          }))
        );
      } else {
        setAccommodationListOptions([]);
      }
    } else {
      setAccommodationListOptions([]);
    }
  }, [accommodations, getAllPricingList, selectedAccommodationType, subCategory]);

  useEffect(() => {
    if (blocksDyna && blocksDyna.blocks) {
      setBlocksListOptions([
        { value: 'Select type', label: 'Select type' },
        ...blocksDyna.blocks.map((item) => ({ value: item.id, label: item.name }))
      ]);
    }
  }, [blocksDyna]);

  useEffect(() => {
    if (PLCbyProjectIdList) {
      const filterList = PLCbyProjectIdList.filter((item) => item.id === parseInt(selectedBlockId));
      const extractPLC = filterList.flatMap((item) => item.plcs);
      const PLCData = extractPLC?.map((plc) => ({ value: plc.id, label: plc.name }));
      setPLCListOptions([{ value: 'Select type', label: 'Select type' }, ...PLCData]);
    }
  }, [PLCbyProjectIdList, selectedBlockId]);

  useEffect(() => {
    if (blocksDyna && blocksDyna.blocks) {
      const filteredBlocks = blocksDyna.blocks.filter(
        (item) => item.id === parseInt(selectedBlockId)
      );
      const extractedUnits = filteredBlocks.flatMap((item) =>
        item.levels.flatMap((level) => level.units)
      );
      const unitsData = extractedUnits?.map((unit) => ({
        value: unit.id,
        label: unit.unitName
      }));
      setUnitListOptions([{ value: 'Select type', label: 'Select type' }, ...unitsData]);
    }
  }, [blocksDyna, selectedBlockId]);

  const handlePreviewAccmmodationIDonProccedbtn = () => {
    setProccedbtn('previewAccommodationById');
    setSubCategory('');
  };

  const handlePreviewUnitIDonProccedbtn = () => {
    setProccedbtn('previewUnitById');
    setSubCategory('');
  };

  const [loadingFactor, setLoadingFactor] = useState('');

  const handleLoadingFactorChange = (e) => {
    let inputVal = e;
    inputVal = inputVal.replace(/\D/g, '');
    const numericVal = parseInt(inputVal, 10);
    if (!isNaN(numericVal) && numericVal > 100) {
      inputVal = '100';
      toast.error('Loading factor cannot exceed 100');
    }
    setLoadingFactor(inputVal);
    setValue('loadingFactor', inputVal);
  };

  const applyAccommodationLoadingFactor = useCallback(
    (accommodation) => {
      const factor = accommodation?.loadingFactor;
      const factorStr =
        factor === null || factor === undefined || factor === '' ? '' : String(factor);
      setLoadingFactor(factorStr);
      setValue('loadingFactor', factorStr);
    },
    [setValue, setLoadingFactor]
  );

  // Reset selected accommodation whenever accommodation type changes
  const handleAccommodationTypeChange = (e) => {
    setSelectedAccommodationType(e.target.value);
    setSelectedAccommodation(null);
    setLoadingFactor('');
    setValue('loadingFactor', '');
  };

  const handleAccommodationChange = (selected) => {
    setSelectedAccommodation(selected || null);
    if (selected) {
      applyAccommodationLoadingFactor(selected);
    } else {
      setLoadingFactor('');
      setValue('loadingFactor', '');
    }
  };

  // Reset selected accommodation whenever rate type changes
  const handleRateTypeChange = (e) => {
    setSubCategory(e.target.value);
    setSelectedAccommodation(null);
    setLoadingFactor('');
    setValue('loadingFactor', '');
  };

  /*
   * convertUmPrice — forward conversion used when SAVING a UM-based rate.
   *
   * The user enters price in UM units (e.g. ₹1000/tago).
   * The backend always stores and calculates using cookie units (e.g. sq/ft).
   *
   * Formula: cookie_price = entered_price / per_unit
   * Example: ₹1000/tago ÷ 10 (1 tago = 10 sq/ft) = ₹100/sq/ft stored
   *
   * UM options are project-scoped and selected from available project measurements.
   * So no sq/ft↔sq/mt cross-conversion is needed here.
   *
   * toFixed(8): preserves 8 decimal places to avoid integer rounding loss.
   * Without this, ₹100/sq/ft stored as 9 (rounded from 9.29) reverses to ₹969 not ₹1000.
   */
  const convertUmPrice = (rawPrice, um) => {
    const perUnit = parseFloat(um?.per_unit) || 1;
    return rawPrice / perUnit;
  };

  /*
   * onSubmit — builds the API payload based on the selected rate type and accommodation type.
   *
   * Payload shape:
   *   - mode: 'sqft' for area-based (cookie + UM), 'accommodation' for flat price
   *   - universal_measurement_id: included only for UM-based rows
   *   - loading_factor: included for cookie-based sqft and UM-based rows
   *   - price / plote_price / construction_price: converted to cookie units for UM rows
   */
  const onSubmit = (data) => {
    if (!selectedAccommodation) return;
    setPreviewData([]);

    const accommodation = selectedAccommodation;
    const basePayload = {
      mode: subCategory === measurementTypeWithoutSlash || isUmBased ? 'sqft' : 'accommodation',
      accommodation_id: accommodation.value,
      project_id: parseInt(projectId),
      // Only UM-based rows carry universal_measurement_id
      ...(isUmBased && selectedUm ? { universal_measurement_id: selectedUm.id } : {})
    };

    if (subCategory === measurementTypeWithoutSlash || isUmBased) {
      basePayload.loading_factor = parseFloat(data.loadingFactor) || 0;

      if (selectedAccommodationType === 'INDEPENDENT') {
        const rawPlotPrice = parseInt(data.plote_price) || 0;
        const rawConstructionPrice = parseInt(data.construction_price) || 0;
        // UM: divide by per_unit to convert to cookie units; cookie-based: use as-is
        basePayload.plote_price = isUmBased
          ? parseFloat(convertUmPrice(rawPlotPrice, selectedUm).toFixed(8))
          : rawPlotPrice;
        basePayload.construction_price = isUmBased
          ? parseFloat(convertUmPrice(rawConstructionPrice, selectedUm).toFixed(8))
          : rawConstructionPrice;
      } else {
        const rawPrice = parseInt(data.rate_per_sqft) || 0;
        // UM: divide by per_unit to convert to cookie units; cookie-based: use as-is
        basePayload.price = isUmBased
          ? parseFloat(convertUmPrice(rawPrice, selectedUm).toFixed(8))
          : rawPrice;
      }
    } else if (subCategory === 'accommodation') {
      // Flat price — no conversion needed for any accommodation type
      if (selectedAccommodationType === 'INDEPENDENT') {
        basePayload.plote_price = parseInt(data.plote_price) || 0;
        basePayload.construction_price = parseInt(data.construction_price) || 0;
      } else {
        basePayload.price = parseInt(data.price) || 0;
      }
    }

    const payloads = [basePayload];

    // previewPayload mirrors basePayload but with human-readable fields for the preview screen
    const previewPayload = {
      project_name: projectName || '',
      accommodation_name: accommodation.label,
      mode: subCategory === measurementTypeWithoutSlash || isUmBased ? 'sqft' : 'accommodation'
    };

    if (subCategory === measurementTypeWithoutSlash || isUmBased) {
      previewPayload.loading_factor = data.loadingFactor;
    }

    if (selectedAccommodationType === 'INDEPENDENT') {
      previewPayload.plote_price = data.plote_price;
      previewPayload.construction_price = data.construction_price;
    } else {
      if (isUmBased) {
        // Show the converted cookie-unit price in the preview (same as what will be stored)
        const rawPrice = parseInt(data.rate_per_sqft) || 0;
        previewPayload.price = parseFloat(convertUmPrice(rawPrice, selectedUm).toFixed(8));
      } else {
        previewPayload.price =
          subCategory === measurementTypeWithoutSlash ? data.rate_per_sqft : data.price;
      }
    }

    setFormPayload({ data: payloads });
    setPreviewData([previewPayload]);
    console.log('payloads === >>>', { payloads });
    console.log('rate type =>', subCategory, '| isUmBased =>', isUmBased, '| selectedUm =>', selectedUm);
    console.log(
      'cookie measurement =>', measurementType,
      '| converted price =>', basePayload.price ?? `plote: ${basePayload.plote_price}, construction: ${basePayload.construction_price}`
    );
    handleCreatePricing({ data: payloads });
  };

  const { mutate: handleCreatePricing } = useMutation({
    mutationFn: (payload) => createPricing(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: async (data) => {
      setIsSubmitting(false);
      toast.success('Pricing created successfully');
      setProccedbtn('');
      setFormPayload({});
      setPreviewData([]);
      createSuccess();
      await triggerProjectStructureReview(
        projectId,
        PROJECT_SETUP_TYPES.ACCOMMODATION_PRICING
      );
    },
    onError: (err) => {
      setIsSubmitting(false);
      console.error('Error creating pricing', err);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating pricing');
    }
  });

  const handleSelectBlockChange = (e) => {
    setSelectedBlockId(e.target.value);
  };

  const handleCreatePricingClick = () => {
    handleCreatePricing(formPayload);
  };

  return (
    <div>
      {onProccedbtn === 'previewAccommodationById' && (
        <PreviewRateAccommodationId
          previewData={previewData}
          isSubmitting={isSubmitting}
          handlePreviewProceed={handleCreatePricingClick}
          onEdit={() => {
            setProccedbtn('');
            setSubCategory('accommodation');
          }}
        />
      )}
      {onProccedbtn === 'previewUnitById' && (
        <PreviewRateUnitID
          previewData={previewData}
          isSubmitting={isSubmitting}
          handlePreviewProceed={handleCreatePricingClick}
          onEdit={() => {
            setProccedbtn('');
            setSubCategory(measurementTypeWithoutSlash);
          }}
        />
      )}
      {!onProccedbtn && (
      <form onSubmit={handleSubmit(onSubmit)}>
        <div>
          <div className="flex flex-col justify-between items-start gap-4">
            <span className="text-lg border-b-[1px] border-stone-200 w-full pb-2">
              Rate Details
            </span>
          </div>

          {/* Rate Type | Accommodation Type | Accommodation Name — selection row */}
          <div className="flex gap-4 mt-4 items-end">
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">Rate Type</label>
              {/*
               * Rate Type dropdown options:
               *   1. Cookie-based  — always shown (e.g. "sq/ft Based")
               *   2. Accommodation Based — always shown
               *   3. Universal Measurements — all project-scoped UMs returned by backend
               */}
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={subCategory}
                onChange={handleRateTypeChange}>
                <option value="">Select rate type</option>
                <option value={measurementTypeWithoutSlash}>{measurementType} Based</option>
                <option value="accommodation">Accommodation Based</option>
                {universalMeasurements.map((um) => (
                  <option key={um.id} value={`um_${um.id}`}>
                    {um.name} Based
                  </option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Type
              </label>
              <select
                className="w-full p-2 border border-gray-300 rounded-md"
                value={selectedAccommodationType}
                onChange={handleAccommodationTypeChange}>
                <option value="GROUP">GROUP</option>
                <option value="INDEPENDENT">INDEPENDENT</option>
              </select>
            </div>
            <div className="flex-1 min-w-0">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Accommodation Name
              </label>
              {/* Only shows accommodations that don't already have a rate list */}
              <Select
                options={accommodationListOptions}
                value={selectedAccommodation}
                onChange={handleAccommodationChange}
                isClearable
                placeholder={
                  accommodationListOptions.length === 0
                    ? 'No available accommodations'
                    : 'Select accommodation'
                }
                noOptionsMessage={() => 'No available accommodations. All have rate lists already.'}
                className="basic-select"
                classNamePrefix="select"
              />
            </div>
          </div>

          {/* Price inputs — shown only after a rate type is selected */}
          {subCategory && (
            <>
              {/* Read-only area info — auto-filled when an accommodation is selected */}
              <div className="flex gap-4 mt-4 items-end">
                <div className="flex-1 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Build up area</label>
                  <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                    {selectedAccommodation
                      ? (selectedAccommodation.buildUpArea ?? selectedAccommodation.build_up_area ?? '—')
                      : '—'}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Plot area</label>
                  <div className="w-full p-2 border border-gray-300 rounded-md bg-gray-50 text-gray-700">
                    {selectedAccommodation
                      ? (selectedAccommodation.plotArea ?? selectedAccommodation.plot_area ?? '—')
                      : '—'}
                  </div>
                </div>
              </div>

              {/* Loading Factor — shown for cookie-based sqft AND UM-based (not for accommodation-based) */}
              {(subCategory === measurementTypeWithoutSlash || isUmBased) && (
                <div className="flex justify-between mt-4">
                  <FormInput
                    width={49}
                    label={'Loading Factor (%)'}
                    register_key={'loadingFactor'}
                    register={register}
                    type={'text'}
                    onType={handleLoadingFactorChange}
                    valuePre={loadingFactor}
                    errors={errors.loadingFactor}
                  />
                </div>
              )}

              {/*
               * Price inputs — layout depends on accommodation type:
               *
               * INDEPENDENT: Plot Price + Construction Price (two fields)
               * GROUP:       Single rate/price field
               *   - Cookie-based sqft:  label "Rate per sq/ft", key 'rate_per_sqft'
               *   - UM-based:           label "Rate per tago",  key 'rate_per_sqft' (same key, different label)
               *   - Accommodation:      label "Price",          key 'price'
               */}
              <div className="flex w-full justify-between mt-4">
                {selectedAccommodationType === 'INDEPENDENT' ? (
                  <div className="flex justify-between mt-4 w-full">
                    <FormInput
                      width={49}
                      label={'Plot Price'}
                      register_key={'plote_price'}
                      register={register}
                      isRequired={true}
                      type={'text'}
                    />
                    <FormInput
                      width={49}
                      label={'Construction Price'}
                      register_key={'construction_price'}
                      register={register}
                      type={'text'}
                    />
                  </div>
                ) : (
                  <FormInput
                    width={49}
                    label={
                      subCategory === measurementTypeWithoutSlash
                        ? `Rate per ${measurementType}`
                        : isUmBased
                        ? `Rate per ${selectedUm?.name || selectedUm?.measurement_type || 'unit'}`
                        : 'Price'
                    }
                    register_key={
                      subCategory === measurementTypeWithoutSlash || isUmBased
                        ? 'rate_per_sqft'
                        : 'price'
                    }
                    isRequired={true}
                    register={register}
                    type={'text'}
                  />
                )}
              </div>

              <div className="w-full flex justify-end items-center space-x-4 mt-8">
                <Button type="reset" displayType="secondary" onClick={onClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!selectedAccommodation}>
                  Proceed
                </Button>
              </div>
            </>
          )}
        </div>
      </form>
      )}
    </div>
  );
};
